/**
 * NavRail — Teams-style icon rail navigation
 *
 * 10 items with icon + label, ~70px wide.
 * Items show/hide based on user permissions.
 * Active item highlighted with accent background.
 */

import { Link, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  MessageSquare,  // Ask Argus
  ShieldCheck,    // Approvals
  Bot,            // Agents
  Wrench,         // Studio
  Plug,           // Connections
  BookOpen,       // Library
  Zap,            // Automate
  LayoutDashboard, // Argus Live (AL-B3 follow-up — rail entry)
  BarChart3,      // Insights
  Shield,         // Trust (tenant)
  UserCog,        // Users + Administrators (sysadmin)
  Settings,       // Admin (tenant) + Settings (sysadmin)
  User,           // Profile (bottom)
  Building2,      // Tenants (sysadmin)
  LogOut,         // Sign out
  Home,           // Sysadmin Home
  Boxes,          // Catalog
  Scale,          // Governance
  Activity,       // Runtime
  ScrollText,     // Audit
  Server,         // Platform
  Sparkles,       // Fine-Tuning (Phase 5a)
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { ArgusEye } from '@/components/icons';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';

// Wrapper to make ArgusEye compatible with LucideIcon interface
const ArgusEyeIcon = ({ className }: { className?: string }) => (
  <ArgusEye state="idle" className={className} />
);
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface NavRailItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  /** Badge count (e.g., pending approvals) */
  badge?: number;
  /** Feature toggle required for the rail item to be shown. */
}

export const TENANT_RAIL_ITEMS: NavRailItem[] = [
  { id: 'ask-argus', label: 'Ask Argus', icon: ArgusEyeIcon as any, path: '/ask-argus' },
  { id: 'approvals', label: 'Approvals', icon: ShieldCheck, path: '/approvals' },
  { id: 'agents', label: 'Agents', icon: Bot, path: '/agents' },
  // Wave 3 b73 — Models is now the first tab inside Studio
  // (/studio renders the assigned-brands list). Dropped from the rail
  // since most of the model lifecycle work happens on the sysadmin
  // side; tenants only consume the catalog.
  { id: 'studio', label: 'Studio', icon: Wrench, path: '/studio' },
  { id: 'connections', label: 'Connections', icon: Plug, path: '/connections' },
  { id: 'library', label: 'Library', icon: BookOpen, path: '/library' },
  { id: 'automate', label: 'Automate', icon: Zap, path: '/automate' },
  // AL-B3 follow-up (2026-05-12) — Argus Live rail entry. The AL-B3 PR
  // added Argus Live to `navigation.ts` (which feeds the legacy
  // `<AppSidebar>` component), but the actually-rendered rail is
  // `<NavRail>` driven off TENANT_RAIL_ITEMS. Click-through reverify
  // 2026-05-12 caught the omission: feature reachable only by typing
  // the URL. The page itself enforces RBAC via `useHasPermission('read',
  // 'argus_live')`, so no `minRole`-style filter is needed here.
  { id: 'argus-live', label: 'Argus Live', icon: LayoutDashboard, path: '/argus-live' },
  { id: 'insights', label: 'Insights', icon: BarChart3, path: '/insights' },
  { id: 'trust', label: 'Trust', icon: Shield, path: '/trust' },
  { id: 'users', label: 'Users', icon: UserCog, path: '/users' },
  { id: 'admin', label: 'Admin', icon: Settings, path: '/admin' },
];

// Sysadmin rail — canonical 10-item set per
// docs/SYSADMIN-PLATFORM-SPEC.md §3 (PR-J amendment 2026-05-08).
// Surfaces only platform-scoped routes; tenant rail items would 401
// INVALID_AUDIENCE.
//
// Order is operational priority. Library was added between Catalog and
// Governance per PR-J: sysadmin authors platform docs, regulations,
// SOPs in the system Library and publishes them; tenants see published
// system content alongside their own. Replaces the planned (and
// dropped) "Knowledge packs" abstraction inside Catalog. The
// catalog/knowledge sub-tab placeholder was removed; the Catalog rail
// no longer carries that route.
//
// Catalog/Governance/Runtime/Audit/Platform/Settings ship as scaffolds
// in §10.2 #5; full module builds land Wave 4. Models sub-tree under
// Catalog deep-links to the existing /sysadmin/models pages so today's
// Models work isn't lost.
export const SYSADMIN_RAIL_ITEMS: NavRailItem[] = [
  { id: 'sysadmin-home',           label: 'Home',           icon: Home,       path: '/sysadmin' },
  { id: 'sysadmin-tenants',        label: 'Tenants',        icon: Building2,  path: '/sysadmin/tenants' },
  // Land on Functions, not Models: the Models sub-tab deep-links OUT to the
  // legacy /sysadmin/models tree which does NOT render the Catalog entity-tab
  // bar, stranding the operator with no way to click through to Tools / Skills
  // / Agents / Prompts. Functions renders the full catalog.tsx tab strip AND is
  // where the AI function-builder helper lives, so the entity tabs are always
  // one click away from the rail.
  { id: 'sysadmin-catalog',        label: 'Catalog',        icon: Boxes,      path: '/sysadmin/catalog/functions' },
  { id: 'sysadmin-library',        label: 'Library',        icon: BookOpen,   path: '/sysadmin/library' },
  { id: 'sysadmin-governance',     label: 'Governance',     icon: Scale,      path: '/sysadmin/governance/guardrails' },
  { id: 'sysadmin-runtime',        label: 'Runtime',        icon: Activity,   path: '/sysadmin/runtime/health' },
  { id: 'sysadmin-audit',          label: 'Audit',          icon: ScrollText, path: '/sysadmin/audit/stream' },
  // Phase 5a — Fine-Tuning rail. Sysadmin-only (the rail file is
  // already platform-scope). Adapters / Training Data / Golden Set /
  // Evaluation. Phase 5b plugs in the actual QLoRA trainer.
  { id: 'sysadmin-fine-tuning',    label: 'Fine-Tuning',    icon: Sparkles,   path: '/sysadmin/fine-tuning/adapters' },
  { id: 'sysadmin-platform',       label: 'Platform',       icon: Server,     path: '/sysadmin/platform/deployment' },
  // `/sysadmin/settings` — the settings INDEX route owns which tab is first
  // and redirects there (settings.index.tsx → /sysadmin/branding). The rail
  // pointed at `/sysadmin/settings/branding`, which is not a route at all
  // (Branding lives at /sysadmin/branding), so the rail's Settings item landed
  // on not-found. One owner for the first tab, and it is not this array.
  { id: 'sysadmin-settings',       label: 'Settings',       icon: Settings,   path: '/sysadmin/settings' },
  { id: 'sysadmin-administrators', label: 'Administrators', icon: UserCog,    path: '/sysadmin/administrators' },
];

// Backwards compat: callers that still import `RAIL_ITEMS` get the tenant
// list. New code should pick the right list explicitly.
const RAIL_ITEMS = TENANT_RAIL_ITEMS;

interface NavRailProps {
  /** Optional: filter items based on permissions */
  visibleItems?: string[];
  /** Optional: badge counts per item */
  badges?: Record<string, number>;
}

export function NavRail({ visibleItems, badges }: NavRailProps) {
  const location = useLocation();
  const { user, isSysadmin, isLoading, signOut } = useAuth();
  // Wave 1a/W1a-22 — swap rail set based on auth context. Sysadmin
  // tokens (audience `argusai-admin`) get the platform rail; tenant
  // tokens get the workspace rail.
  //
  // Foundations 1 / B0-T1: no rail is feature-gated any more. Every rail is
  // simply on (decisions.md 2026-09-08, "Features, flags and plans"); what a
  // person may DO inside one is a permission check on the page, and the
  // per-request fetch of `/admin/settings/features` that used to filter this
  // list is gone with the endpoint.
  const baseItems = isSysadmin ? SYSADMIN_RAIL_ITEMS : TENANT_RAIL_ITEMS;
  const items = visibleItems
    ? baseItems.filter(item => visibleItems.includes(item.id))
    : baseItems;

  // Issue #87 — delegate to the AuthProvider's signOut so the refresh
  // token is revoked server-side, React state resets, and the query
  // cache is dropped. The old implementation only removed localStorage
  // tokens and left the protected shell visible.
  const handleSignOut = () => {
    void signOut();
  };

  const initials =
    user.firstName && user.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : user.email.slice(0, 2).toUpperCase();
  const displayName =
    user.firstName || user.lastName
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      : user.email;

  return (
    <div className="w-[76px] bg-sidebar border-r border-sidebar-border flex flex-col items-center py-3 h-full shrink-0">
      {/* Rail items */}
      <nav className="flex-1 flex flex-col items-center gap-1 w-full px-2 overflow-y-auto">
        {items.map((item) => {
          // Home items (/, /sysadmin) match exactly — otherwise their
          // prefix would swallow every sub-route. Tabs that point at a
          // sub-tree (e.g., /sysadmin/catalog/models) match the rail
          // item by their parent prefix (/sysadmin/catalog).
          const homePaths = new Set(['/', '/sysadmin']);
          const matchPrefix = item.path.split('/').slice(0, 3).join('/'); // e.g., /sysadmin/catalog
          const isActive = homePaths.has(item.path)
            ? location.pathname === item.path
            : location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/') ||
              (matchPrefix !== item.path && location.pathname.startsWith(matchPrefix + '/'));
          const badgeCount = badges?.[item.id] ?? item.badge;
          const Icon = item.icon;

          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                "relative flex flex-col items-center justify-center gap-0.5 w-full py-2 rounded-xl text-[10px] font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={isActive ? 2.2 : 1.8} />
              <span className="leading-tight text-center">{item.label}</span>

              {/* Badge */}
              {badgeCount != null && badgeCount > 0 && (
                <span className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      <Separator className="mx-3 my-1" />
      <div className="px-1.5 w-full">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex flex-col items-center justify-center gap-0.5 w-full py-2 rounded-xl text-[10px] font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-semibold text-primary">
                {initials}
              </div>
              <span className="leading-tight">Profile</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-52">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              {isSysadmin && (
                <p className="text-[10px] text-primary mt-1 font-medium uppercase tracking-wider">
                  Sysadmin
                </p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem><User className="h-4 w-4 mr-2" /> My Profile</DropdownMenuItem>
            <DropdownMenuItem><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export { RAIL_ITEMS };
